import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MFASetupPage } from "../../../components/pages/mfa/setup";
import { MFAExpiredPage } from "../../../components/pages/mfa/expired";
import { NoMenuNavigationBar } from "../../../components/parts/nomenu-navigation-bar";
import { Loading } from "../../../../../react-components/lu-component/src/index";
import { post } from "../../../lib/request";
import { endpoint } from "../../../../../server/router/endpoint";
import { SetupMFAResponse, VerifySetupMFARequest, VerifySetupMFAResponse } from "../../../../../shared/mfa";

type LocationState = { token?: string } | null;

const verifyFailedMessage = "認証に失敗しました。認証アプリに表示されている最新のコードを入力してください。";

export const MFASetupContainer = () => {
	const navigate = useNavigate();
	// メール認証後に渡される一時セッション用トークン（リロードすると失われる）
	const { token } = (useLocation().state as LocationState) ?? {};
	const [setup, setSetup] = React.useState<SetupMFAResponse>();
	const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>();
	const [redirectUrl, setRedirectUrl] = React.useState<string>();
	const [isExpired, setIsExpired] = React.useState<boolean>(!token);
	const [error, setError] = React.useState<string>();
	const [isPending, setIsPending] = React.useState<boolean>(false);

	const options = React.useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

	React.useEffect(() => {
		if (!token) return;
		post<SetupMFAResponse>(endpoint.mfaSetup, {}, options)
			.then((response) => setSetup(response.data))
			.catch(() => setIsExpired(true));
	}, [options, token]);

	const onSubmit = React.useCallback(
		(code: string) => {
			const request: VerifySetupMFARequest = { setupToken: setup?.setupToken, token: code };
			setIsPending(true);
			return post<VerifySetupMFAResponse>(endpoint.mfaSetupVerify, request, options)
				.then((response) => {
					setError(undefined);
					setRecoveryCodes(response.data.recoveryCodes);
					setRedirectUrl(response.data.redirectUrl);
				})
				.catch((error) => {
					setError(verifyFailedMessage);
					throw error;
				})
				.finally(() => setIsPending(false));
		},
		[options, setup],
	);
	// セットアップ完了時にログイン済みになるため、ログイン前にアクセスしたページ（なければトップページ）へ
	const onComplete = React.useCallback(() => navigate(redirectUrl || "/", { replace: true }), [navigate, redirectUrl]);

	return (
		<>
			<NoMenuNavigationBar />
			{isExpired ? (
				<MFAExpiredPage />
			) : setup ? (
				<MFASetupPage
					error={error}
					isPending={isPending}
					manualEntryKey={setup.manualEntryKey}
					onComplete={onComplete}
					onSubmit={onSubmit}
					qrCodeUrl={setup.qrCodeUrl}
					recoveryCodes={recoveryCodes}
				/>
			) : (
				<Loading loading={true} />
			)}
		</>
	);
};
